/**
 * File: MandateFormValidation
 *
 * Description:
 * This module defines the form structure and validation rules for the Mandate form.
 * It includes:
 * - TypeScript type `MandateFormValues` describing the expected form data shape
 * - A Yup validation schema `MandateValidationSchema` enforcing field constraints
 *
 * Validation rules ensure:
 * - Required fields are filled
 * - Numeric fields contain valid numbers
 * - Dates are valid
 * - Strings respect character rules and max length
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */

import * as Yup from 'yup';
import {
    stringWithNoSpecialChars,
    validateAmount,
    validateDate,
    validateFloat,
    validateNumericId,
    validateOperationType,
    validatePropertyUsageType,
    validateTargetType,
} from "@/Shared/Helpers";
import { MandateEnums } from '@/Shared';

/**
 * Type: MandateFormValues
 *
 * Represents the shape of the Mandate form data.
 */
export type MandateFormValues = {
    operationType?: string,    // Operation type (buy/sell/etc.)
    agentId: number,           // ID of the referent agent
    startAt: string,           // Start date of the mandate
    endAt: string,             // End date of the mandate
    referrer: string,          // Optional referrer information
    notes: string,             // Optional notes field
    feeRate: number,           // Fee rate percentage
    applicableVat : number,
    contacts: string[], 
    propertyId: number,   
    needId: number,   
    mandateType: string,

    agencyComTtc: string,
    totalPriceTtc : string

};


/**
 * Function: MandateValidationSchema
 *
 * Returns a Yup validation schema for the Mandate form.
 * 
 * @param viewEnums - Provides available enums for operationType validation
 * @returns Yup.ObjectSchema<MandateFormValues>
 *
 * Validation rules:
 * - `contactId` and `agentId` must be numeric and are required
 * - `operationType` must match allowed operation types
 * - `startAt` and `endAt` must be valid dates and required
 * - `referrer` and `notes` are optional but limited in length and character content
 * - `feeRate` must be a valid float and is required
 */
export const MandateValidationSchema = (viewEnums: MandateEnums) => Yup.object({

    operationType: validateOperationType(viewEnums.OperationTypeEnum),

    mandateType: validateTargetType(viewEnums.MandateTypeEnum),

    agentId: validateNumericId().required('Le référent est requis.'),

    startAt: validateDate().required('La date de début est requise.'),

    endAt: validateDate().required('La date de fin est requise.'),

    referrer: stringWithNoSpecialChars({ maxLength: 255 }).notRequired(),

    notes: stringWithNoSpecialChars({ maxLength: 500 }).notRequired(),

    feeRate: validateFloat().required('Le taux honoraires est requis'),

    applicableVat: validateFloat().required('Le TVA applicable est requis'),

    propertyId: Yup.number(),

    needId: Yup.number(),

    agencyComTtc: validateAmount().nonNullable().notRequired(),

    totalPriceTtc: validateAmount().nonNullable().notRequired(),

    
}).test('validate-target', function (values) {
    const type = values.mandateType ?? '';

    if (['sale_mandate', 'rent_management_mandate'].includes(type)) {
        if (!values?.propertyId) {
            return this.createError({
                path: 'propertyId',
                message: 'Vous devez sélectionner un bien.',
            });
        }
    }

    if (['search_property_rent_mandate'].includes(type)) {
        if (!values?.needId) {
            return this.createError({
                path: 'needId',
                message: 'Vous devez sélectionner un besoin.',
            });
        }
    }

    return true;
});
